import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import FloatingActionButton from './FloatingActionButton';
import AddAddressModal from './AddAddressModal';
import LoadingAnimation from './LoadingAnimation';
import { fetchAddresses, deleteAddress } from '../api/github';

export default function AddressesList({ isMobile, refreshTrigger }) {
  const [addresses, setAddresses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadAddresses = async () => {
    setLoading(true);
    try {
      const data = await fetchAddresses();
      setAddresses(data);
    } catch (error) {
      console.error('Ошибка загрузки адресов:', error);
      setSnackbar({ open: true, message: 'Адреса юклашда хатолик юз берди.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, [refreshTrigger]);

  const filteredAddresses = addresses.filter((address) =>
    address.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleModalOpen = (address = null) => {
    setEditingAddress(address);
    setModalOpen(true);
  };

  const handleModalClose = (shouldRefresh) => {
    setModalOpen(false);
    setEditingAddress(null);
    if (shouldRefresh) {
      loadAddresses();
      setSnackbar({
        open: true,
        message: editingAddress ? 'Манзил ўзгартирилди.' : 'Манзил сақланди.',
        severity: 'success',
      });
    }
  };

  const handleDeleteClick = (address) => {
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (addressToDelete) {
      try {
        await deleteAddress(addressToDelete.id);
        loadAddresses();
        setSnackbar({ open: true, message: 'Манзил ўчирилди.', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: 'Манзилни ўчиришда хатолик юз берди.', severity: 'error' });
      }
    }
    setDeleteDialogOpen(false);
    setAddressToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAddressToDelete(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: 2,
          gap: 2,
        }}
      >
        <TextField
          placeholder="Манзил қидириш..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          fullWidth
          sx={{ maxWidth: '100%' }}
        />
        <FloatingActionButton
          onClick={() => handleModalOpen()}
          tooltip="Манзил қўшиш"
        />
      </Box>

      {loading ? (
        <LoadingAnimation loading={loading} />
      ) : filteredAddresses.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          Манзиллар топилмади.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <List sx={{ width: '100%' }}>
            {filteredAddresses.map((address) => (
              <ListItem key={address.id} divider>
                <ListItemText
                  primary={address.address}
                  secondary={`Қўшилган: ${new Date(address.createdAt).toLocaleDateString('uz-UZ')}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleModalOpen(address)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteClick(address)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Grid>
      )}

      <AddAddressModal
        open={modalOpen}
        onClose={handleModalClose}
        EditAddress={editingAddress}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Манзилни ўчириш</DialogTitle>
        <DialogContent>
          <Typography>
            {addressToDelete
              ? `Сиз ростдан "${addressToDelete.address}" манзилни ўчирмоқчимисиз?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Бекор қилиш</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Ўчириш
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}